import { Stack, Theme, Typography, useMediaQuery } from "@mui/material";
import { makeStyles } from "@mui/styles";
import LibraryBooksTwoToneIcon from "@mui/icons-material/LibraryBooksTwoTone";

import Excel from "exceljs";
import { saveAs } from "file-saver";
import moment from "moment";

import Button from "components/button";

import { AnyObject } from "immer/dist/internal";
import {
  meridianDateFormat,
  formatTime,
  validateDateTimeFormat,
  getPoweredByText,
} from "../helperFunctions";
import {
  headerButtonMobile,
  headerButtonExcelExport,
} from "features/businessListing/index.styles";
import {
  OrderListingResponseResult,
  ProductResponseItem,
  OrderListingResponseOrderDetail,
  MenuCategoryResponseResult,
  BrandsResponseProps,
  AbandonedCartOrder,
  ReservationsResponseResult,
  StaffAttendanceResult,
  DeliveryAreaDetails,
  DeliveryAddress,
} from "utils/types";
import { ReviewProps } from "utils/types/review";
import { ReportResult } from "utils/types/Reports";
import { setColumnWidth } from "utils/helperFunctions";
import { ExportData } from "features/reviews/businessReviews/index.types";
import { SelectedStaffDetails } from "features/pos/index.types";
import { StockProps } from "features/inventory/index.types";
import { useSelector } from "store";

const useStyles = makeStyles(() => ({
  libraryBookIcon: {
    color: "#616161",
  },
  excelExportFile: {
    width: "104px",
    height: " 20px",
    fontFamily: "Roboto",
    fontStyle: "normal",
    fontWeight: 500,
    fontSize: "14px",
    lineHeight: "20px",
  },
}));

type TableData = {
  header: string;
  key: string;
};

type ExcelExportPropsType = {
  tableData: TableData[];
  listingData?:
    | OrderListingResponseResult[]
    | ProductResponseItem[]
    | ReviewProps[]
    | MenuCategoryResponseResult[]
    | BrandsResponseProps[]
    | ReportResult[]
    | ExportData[]
    | AbandonedCartOrder[]
    | ReservationsResponseResult[]
    | StaffAttendanceResult[]
    | SelectedStaffDetails
    | StockProps[]
    | DeliveryAddress[]
    | DeliveryAreaDetails[];
  orderDetailData?: OrderListingResponseResult;
  selectedBranch?: string;
  exportType: string;
  buttonText?: string;
  setExportType?: React.Dispatch<React.SetStateAction<string>>;
  disabled?: boolean;
  dynamicExportHeading?: string;
  showKeyasColumn?: boolean;
};

const ExcelExport = ({
  tableData,
  listingData,
  orderDetailData,
  selectedBranch,
  buttonText,
  exportType,
  setExportType,
  disabled,
  dynamicExportHeading,
  showKeyasColumn,
}: ExcelExportPropsType) => {
  const isScreenSmall = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );
  const headerButtonStyles = isScreenSmall
    ? headerButtonMobile
    : headerButtonExcelExport;
  const classes = useStyles(),
    { startDate, endDate, decimalPlaces } = useSelector(
      (state) => state.global
    );

  const fileExport = async () => {
    const { eatout_name } = JSON.parse(localStorage.getItem("businessInfo")!);
    const workSheetName = `${exportType}`;
    const workBookName = `${eatout_name}`;

    const workBook = new Excel.Workbook();
    const workSheet = workBook.addWorksheet(workSheetName);
    const isAlphabetic = (string: string) => {
      const pattern = /^[a-zA-Z]+$/;
      return !pattern.test(string) && string !== "";
    };
    const convertToNumber = (value: number) => {
      if (!isNaN(value)) {
        return Number(value);
      }
      return value;
    };
    switch (exportType) {
      case "OrdersList": {
        // making deep copy of data
        const orderListingData = JSON.parse(JSON.stringify(listingData));

        workSheet.columns = tableData;

        workSheet.getRow(1).font = { bold: true };

        // loop through data and add each one to worksheet
        (orderListingData as OrderListingResponseResult[])?.forEach(
          (singleOrder) => {
            // formatting placement date
            singleOrder["date"] = validateDateTimeFormat(singleOrder?.date);

            // Overwrite cancel to Cancelled on Column Status while exporting excel
            if (singleOrder["status"] === "Cancel") {
              singleOrder["status"] = "Cancelled";
            }

            // Write Pre-Authroized in Order Status Column while exporting excel
            if (
              singleOrder["pre_auth"] === "1" &&
              singleOrder["status"] === "Pending"
            ) {
              singleOrder["status"] = "Pre-Authorized";
            }

            // adding delivery/pickup time
            if (singleOrder?.order_type_flag === "0") {
              singleOrder["delivery_date"] = validateDateTimeFormat(
                singleOrder?.delivery_date
              );
            } else {
              singleOrder["delivery_date"] = validateDateTimeFormat(
                singleOrder?.pickup_time
              );
            }

            // adding products details
            singleOrder["order_items"] = singleOrder.order_detail
              .map((item) => {
                return `${item.item_name} | ${item.quantity} | ${item.total}`;
              })
              .toString();

            workSheet.addRow(singleOrder);
          }
        );

        // set the column width according to max cell content
        setColumnWidth(workSheet);

        break;
      }
      case "ReviewList": {
        workSheet.columns = tableData;

        workSheet.getRow(1).font = { bold: true };

        // loop through all of the columns and set the alignment with width.
        workSheet.columns.forEach((column) => {
          column.width = column.header!.length + 10;
          column.alignment = { horizontal: "center" };
        });
        let row: { [x: string]: string | number } = {};

        // loop through data and add each one to worksheet
        (listingData as ReviewProps[])?.forEach((item: any) => {
          tableData.forEach((obj) => {
            if (obj.key == "datetime") {
              return (row[obj.key] =
                item.date && moment(item.date).format("MMM Do, YYYY hh:mm a"));
            }
            if (obj.key == "customer_name") {
              return (row[obj.key] = item.user_name);
            }
            // all other column values
            return (row[obj.key] = item[obj.key]);
          });

          workSheet.addRow(row);
        });

        break;
      }
      case "BusinessReview": {
        workSheet.columns = tableData;

        workSheet.getRow(1).font = { bold: true };

        // loop through all of the columns and set the alignment with width.
        workSheet.columns.forEach((column) => {
          column.width = column.header!.length + 10;
          column.alignment = { horizontal: "center" };
        });
        let row: { [rowData: string]: string | number } = {};

        // loop through data and add each one to worksheet
        (listingData as ExportData[])?.forEach((item: any) => {
          tableData.forEach((obj) => {
            // all other column values
            return (row[obj.key] = item[obj.key]);
          });
          workSheet.addRow(row);
        });

        break;
      }
      case "stockListing": {
        workSheet.mergeCells("A1:L1");
        workSheet.mergeCells("A2:L2");

        workSheet.getRow(1).getCell(1).value =
          "Please do not change any column except new Quantity to aviod the wrong inventory update and use this format YYYY/MM/DD for expiry date";

        workSheet.getRow(1).getCell(1).font = {
          bold: true,
          color: { argb: "DB154D" },
          size: 14,
        };

        workSheet.getRow(1).getCell(1).alignment = {
          vertical: "middle",
          horizontal: "center",
        };

        workSheet.getRow(1).getCell(1).border = {
          top: { style: "thick" },
          left: { style: "thick" },
          bottom: { style: "thick" },
          right: { style: "thick" },
        };

        // adding column keys to add data
        workSheet.columns = tableData.map((obj) => ({
          key: obj.key,
        }));

        // adding table column heads
        workSheet.getRow(workSheet.rowCount + 1).values = tableData.map(
          (obj) => obj.header
        );

        let row: { [x: string]: string | number } = {};

        (listingData as StockProps[])?.forEach(
          (item: any, outerIndex: number) => {
            tableData.forEach((obj) => {
              // discount_start date column values
              if (obj.key === "expiry_date" && item.expiry_date) {
                return (row[obj.key] = moment(item.expiry_date).format(
                  "MM/DD/YYYY"
                ));
              }

              // all other column values
              return (row[obj.key] = isAlphabetic(item[obj.key])
                ? convertToNumber(item[obj.key])
                : item[obj.key]);
            });

            // add the computed single row to the table
            workSheet.addRow(row);
          }
        );

        break;
      }
      case "ProductListing": {
        workSheet.mergeCells("A1:W1");
        workSheet.mergeCells("A2:W2");

        workSheet.getRow(1).getCell(1).value =
          "Please follow the date format MM/DD/YYYY & Do not update ID's columns and category column";

        workSheet.getRow(1).getCell(1).font = {
          bold: true,
          color: { argb: "DB154D" },
          size: 14,
        };

        workSheet.getRow(1).getCell(1).alignment = {
          vertical: "middle",
          horizontal: "center",
        };

        workSheet.getRow(1).getCell(1).border = {
          top: { style: "thick" },
          left: { style: "thick" },
          bottom: { style: "thick" },
          right: { style: "thick" },
        };

        // adding column keys to add data
        workSheet.columns = tableData.map((obj) => ({
          key: obj.key,
        }));

        // adding table column heads
        workSheet.getRow(workSheet.rowCount + 1).values = tableData.map(
          (obj) => obj.header
        );

        let row: { [x: string]: string | number } = {};

        (listingData as ProductResponseItem[])?.forEach((item: any) => {
          tableData.forEach((obj) => {
            // brand name column values
            if (obj.key === "item_brand" && Array.isArray(item[obj.key])) {
              return (row[obj.key] = item.item_brand[0].brand_name);
            }

            // discount_start date column values
            if (obj.key === "discount_start_at" && item.discount_start_at) {
              return (row[obj.key] = moment(item.discount_start_at).format(
                "MM/DD/YYYY"
              ));
            }

            // discount_expiry date column values
            if (obj.key === "discount_expiry" && item.discount_expiry) {
              return (row[obj.key] = moment(item.discount_expiry).format(
                "MM/DD/YYYY"
              ));
            }

            // description column values
            if (obj.key === "desc" && item.desc) {
              return (row[obj.key] = item.desc.replace(
                /<\/?([a-zA-Z_]+?)>/g,
                ""
              ));
            }

            // all other column values
            return (row[obj.key] = isAlphabetic(item[obj.key])
              ? convertToNumber(item[obj.key])
              : item[obj.key]);
          });

          // add the computed single row to the table
          workSheet.addRow(row);
        });

        break;
      }
      case "SEOListing": {
        workSheet.columns = tableData;

        workSheet.getRow(1).font = { bold: true };

        // adding column keys to add data
        workSheet.columns = tableData.map((obj) => ({
          key: obj.key,
        }));

        let row: { [x: string]: string | number } = {};

        (listingData as any)?.forEach((item: any) => {
          tableData.forEach((obj) => {
            // for columns inside the 'seo' object
            if (
              obj.key === "title" ||
              obj.key === "h1" ||
              obj.key === "keywords" ||
              obj.key === "description"
            ) {
              row[obj.key] = item.seo?.[obj.key] || "";
            } else {
              // all other column values
              row[obj.key] = item[obj.key];
            }
          });

          // add the computed single row to the table
          workSheet.addRow(row);
        });

        break;
      }
      case "ListingExport": {
        workSheet.columns = tableData;

        workSheet.getRow(1).font = { bold: true };

        // adding column keys to add data
        workSheet.columns = tableData.map((obj) => ({
          key: obj.key,
        }));

        let row: { [x: string]: string | number } = {};

        (
          listingData as ReservationsResponseResult[] | AbandonedCartOrder[]
        )?.forEach((item: any) => {
          tableData.forEach((obj) => {
            // all other column values
            return (row[obj.key] = item[obj.key]);
          });

          // add the computed single row to the table
          workSheet.addRow(row);
        });

        // set the column width according to max cell content
        setColumnWidth(workSheet);

        break;
      }
      case "Products":
      case "Daily Sales":
      case "Custom Report": {
        workSheet.mergeCells("A1:L1");
        workSheet.mergeCells("A2:L2");
        workSheet.mergeCells("A3:L3");

        workSheet.getRow(1).getCell(1).value = `${eatout_name}`;
        workSheet.getRow(2).getCell(1).value = dynamicExportHeading
          ? dynamicExportHeading
          : `${selectedBranch} ${selectedBranch && "-"} ${exportType}`;

        workSheet.getRow(3).getCell(1).value = `${startDate} To ${endDate}`;

        workSheet.getRow(1).getCell(1).font = {
          bold: true,
          color: { argb: "DB154D" },
          size: 18,
        };
        workSheet.getRow(2).getCell(1).font = {
          bold: true,
          size: 18,
        };
        workSheet.getRow(3).getCell(1).font = {
          bold: true,
          size: 18,
        };

        workSheet.getRow(1).getCell(1).alignment = {
          vertical: "middle",
          horizontal: "center",
        };
        workSheet.getRow(2).getCell(1).alignment = {
          vertical: "middle",
          horizontal: "center",
        };
        workSheet.getRow(3).getCell(1).alignment = {
          vertical: "middle",
          horizontal: "center",
        };
        // adding column keys to add data
        workSheet.columns = tableData.map((obj, index) => {
          return { key: obj.key };
        });

        // adding table column heads
        workSheet.getRow(workSheet.rowCount + 2).values = tableData.map(
          (heading) => {
            return heading.header;
          }
        );

        // adding styles to column headers
        let headerColumn = 1;
        while (headerColumn < 13) {
          workSheet.getRow(5).getCell(headerColumn).alignment = {
            vertical: "middle",
            horizontal: "center",
          };

          workSheet.getRow(5).getCell(headerColumn).font = {
            bold: true,
            size: 13,
          };
          headerColumn++;
        }

        const getRecords = (obj: any) => {
          if (obj?.value && typeof obj?.value !== "string") {
            return obj.key == "Count"
              ? obj.value
              : obj.value.toFixed(decimalPlaces);
          } else if (obj?.value == "0") {
            return obj.value;
          } else if (obj?.key == "Signup Datetime") {
            return moment(obj.value).format("MMM Do, YYYY hh:mm a");
          } else if (typeof obj?.value == "string") {
            return obj.value;
          } else {
            return "";
          }
        };

        // populating data in table rows
        let row: { [x: string]: any } = {};
        const lastRowTotal: any = [];

        let appendFirstColumn = false;
        if (showKeyasColumn) {
          appendFirstColumn = true;
        }

        (listingData as ReviewProps[])?.forEach(
          (item: { [x: string]: any }, outerIndex: number) => {
            // content
            tableData.forEach((obj, inner_index: number) => {
              // headings
              if (obj.header === "Net Sale Items") {
                row[obj.key] = Number(
                  Number(item["sub_total"]?.value) -
                    Number(item["discount_value"]?.value)
                ).toFixed(decimalPlaces);
              } else if (obj.header == "Creation Date") {
                row[obj.key] = moment(item.key).format("MMM D, YYYY");
              } else if (obj.key == "status_date") {
                row[obj.key] = moment(item[obj.key].value).format(
                  "MMM D, YYYY"
                );
              } else if (appendFirstColumn && item[obj.key]?.key == undefined) {
                appendFirstColumn = false;
                row[obj.key] = item.key;
              } else if (obj.key == "category_name") {
                row[obj.key] = item[obj.key].value;
              } else {
                row[obj.key] = getRecords(item[obj.key]);
              }
            });

            if (showKeyasColumn) {
              appendFirstColumn = true;
            }

            // add the computed single row to the table
            workSheet.addRow(row);

            const valuesArray = Object.values(row);
            // adding styles to content
            let cellNo = 1;
            while (cellNo < 13) {
              if (Number(valuesArray[cellNo - 1])) {
                workSheet.getRow(6 + outerIndex).getCell(cellNo).alignment = {
                  vertical: "middle",
                  horizontal: "right",
                };
              } else if (Number(valuesArray[cellNo - 1]) == 0) {
                workSheet.getRow(6 + outerIndex).getCell(cellNo).alignment = {
                  vertical: "middle",
                  horizontal: "right",
                };
              } else {
                workSheet.getRow(6 + outerIndex).getCell(cellNo).alignment = {
                  vertical: "middle",
                  horizontal: "left",
                };
              }
              cellNo++;
            }
          }
        );

        // calculate Totals
        tableData.forEach((header) => {
          // headings
          let total: number = 0;
          (listingData as ReviewProps[])?.forEach(
            (item: { [x: string]: any }) => {
              if (header.header === "Net Sale Items") {
                total += Number(
                  Number(item["sub_total"]?.value) -
                    Number(item["discount_value"]?.value)
                );
              } else if (
                item[header.key]?.value &&
                typeof item[header.key]?.value !== "string"
              ) {
                total += item[header.key]?.value;
              }
            }
          );

          lastRowTotal.push({
            column: header.key,
            value:
              header.key == "doc_count" ? total : total.toFixed(decimalPlaces),
          });
        });
        //
        workSheet.getRow(workSheet.rowCount + 1).values = lastRowTotal.map(
          (obj: any) => {
            if (obj.column == "creation_date" || obj.column == "menu_items") {
              return "Total";
            } else if (
              obj.column == "status_date" ||
              obj.column == "category_name"
            ) {
              return "";
            }
            return obj.value;
          }
        );

        // Formating the Totals Row
        let totalsRow = 1;
        while (totalsRow < 13) {
          workSheet.getRow(workSheet.rowCount).getCell(totalsRow).alignment = {
            vertical: "middle",
            horizontal: "right",
          };

          workSheet.getRow(workSheet.rowCount).getCell(totalsRow).font = {
            bold: true,
            size: 13,
          };
          totalsRow++;
        }

        // adding company name at the end of sheet
        let copyRightCell = workSheet.rowCount + 3;

        workSheet.mergeCells(`A${copyRightCell}:M${copyRightCell}`);

        workSheet.getRow(copyRightCell).getCell(1).value = getPoweredByText();

        workSheet.getRow(copyRightCell).getCell(1).font = {
          bold: true,
          size: 13,
        };

        workSheet.getRow(copyRightCell).getCell(1).alignment = {
          vertical: "middle",
          horizontal: "center",
        };

        tableData.map((obj, index) => {
          workSheet.getColumn(index + 1).width = 16;
        });

        break;
      }
      case "OrderDetail": {
        workSheet.mergeCells("A1:M1");
        workSheet.mergeCells("A2:M2");

        workSheet.mergeCells("B3:M3");
        workSheet.mergeCells("B4:M4");
        workSheet.mergeCells("B5:M5");
        workSheet.mergeCells("B6:M6");
        workSheet.mergeCells("B7:M7");
        workSheet.mergeCells("B8:M8");
        workSheet.mergeCells("B9:M9");
        workSheet.mergeCells("B10:M10");
        workSheet.mergeCells("B11:M11");
        workSheet.mergeCells("B12:M12");
        workSheet.mergeCells("B13:M13");
        workSheet.mergeCells("B14:M14");

        workSheet.getRow(1).getCell(1).value = `${eatout_name}`;
        workSheet.getRow(1).getCell(1).font = {
          bold: true,
          color: { argb: "DB154D" },
          size: 22,
        };

        workSheet.getRow(1).getCell(1).alignment = {
          vertical: "middle",
          horizontal: "center",
        };

        workSheet.getRow(1).getCell(1).border = {
          top: { style: "thick" },
          left: { style: "thick" },
          bottom: { style: "thick" },
          right: { style: "thick" },
        };

        // starting order details information rows
        workSheet.getRow(3).getCell(1).value = "Order #";
        workSheet.getRow(3).getCell(2).value = `${orderDetailData?.order_id}`;

        workSheet.getRow(4).getCell(1).value = "Payment Type";
        workSheet
          .getRow(4)
          .getCell(2).value = `${orderDetailData?.payment_type}`;

        workSheet.getRow(5).getCell(1).value = "Order Type";
        workSheet.getRow(5).getCell(2).value = `${orderDetailData?.order_type}`;

        workSheet.getRow(6).getCell(1).value = "Status";
        workSheet.getRow(6).getCell(2).value = `${
          orderDetailData?.status === "Cancel"
            ? "Cancelled"
            : orderDetailData?.status
        }`;

        workSheet.getRow(7).getCell(1).value = "Receipt #";
        workSheet.getRow(7).getCell(2).value = `${orderDetailData?.receipt_no}`;

        workSheet.getRow(8).getCell(1).value = "Date";
        workSheet.getRow(8).getCell(2).value = moment(
          orderDetailData?.date
        ).format("MMM Do, YY hh:mm a");

        if (
          orderDetailData?.order_type_flag === "1" && // for delivery (0) & for pickup (1)
          orderDetailData?.pickup_time &&
          orderDetailData?.pickup_time !== "0000-00-00 00:00:00"
        ) {
          workSheet.getRow(9).getCell(1).value = "Pickup Time";
          workSheet.getRow(9).getCell(2).value = validateDateTimeFormat(
            orderDetailData?.pickup_time
          );
        } else if (
          orderDetailData?.order_type_flag === "0" && // for delivery (0) & for pickup (1)
          orderDetailData?.delivery_date &&
          orderDetailData?.delivery_date !== "0000-00-00 00:00:00"
        ) {
          workSheet.getRow(9).getCell(1).value = "Delivery Time";
          workSheet.getRow(9).getCell(2).value = validateDateTimeFormat(
            orderDetailData?.delivery_date
          );
        }

        workSheet.getRow(10).getCell(1).value = "Name";
        workSheet.getRow(10).getCell(2).value = `${orderDetailData?.name}`;

        workSheet.getRow(11).getCell(1).value = "Address";
        workSheet.getRow(11).getCell(2).value = `${orderDetailData?.address}`;

        workSheet.getRow(12).getCell(1).value = "Email";
        workSheet
          .getRow(12)
          .getCell(2).value = `${orderDetailData?.user_email}`;

        workSheet.getRow(13).getCell(1).value = "Landline";
        workSheet
          .getRow(13)
          .getCell(2).value = `${orderDetailData?.landline_number}`;

        workSheet.getRow(14).getCell(1).value = "Phone";
        workSheet
          .getRow(14)
          .getCell(2).value = `${orderDetailData?.mobile_number}`;

        // adding styles to order details information rows
        let headerRows = 3;
        while (headerRows < 15) {
          workSheet.getRow(headerRows).getCell(1).font = {
            bold: true,
            size: 13,
          };

          workSheet.getRow(headerRows).getCell(2).font = {
            size: 12,
          };

          headerRows++;
        }

        // adding column keys to add data
        workSheet.columns = tableData.map((obj) => ({
          key: obj.key,
        }));

        // adding table column heads
        workSheet.getRow(workSheet.rowCount + 2).values = tableData.map(
          (obj) => obj.header
        );

        // format option sets into comma separated string
        const formatOptionSet = () => {
          let optionSetArr = "";
          orderDetailData &&
            orderDetailData.order_detail.map(
              (item: OrderListingResponseOrderDetail) => {
                let row = item;
                let textToShow = "";
                let options: AnyObject = {};
                let optionSets: any = [];
                let innerOptions: any = [];
                if (row.options !== "") {
                  options = JSON.parse(row.options);

                  Object.keys(options).forEach((key) => {
                    for (let i = 0; i < options[key].length; i++) {
                      textToShow += options[key][i].name + ",";
                      optionSets.push(options[key][i].name);

                      let inner_options = options[key][i].inner_options;
                      for (let property in inner_options) {
                        for (
                          let j = 0;
                          j < inner_options[property].length;
                          j++
                        ) {
                          innerOptions.push(inner_options[property][j].name);
                        }
                      }
                    }
                  });
                }

                optionSets.map((f: any, j: number) =>
                  innerOptions.length > 0
                    ? (optionSetArr =
                        optionSetArr + `${f}( ${innerOptions.join()} ),`)
                    : (optionSetArr = optionSetArr + `${f},`)
                );
              }
            );
          return optionSetArr;
        };

        // populating data in table rows
        let row: { [x: string]: string | any } = {};
        let setOptionSet = "";
        orderDetailData?.order_detail?.forEach(
          // orderDetailData?.forEach(
          (item: { [x: string]: string | number | any }) => {
            tableData.forEach((obj) => {
              if (obj.key == "options") {
                setOptionSet = item[obj.key];
                if (
                  setOptionSet &&
                  Object.keys(item[obj.key]).length !== 0 &&
                  setOptionSet !== "[]" &&
                  setOptionSet !== "{}"
                ) {
                  row[obj.key] = formatOptionSet();
                  return row[obj.key];
                } else {
                  return (row[obj.key] = "");
                }
              }
              return (row[obj.key] = item[obj.key]);
            });

            // add the computed single row to the table
            workSheet.addRow(row);
          }
        );

        // order details footer information
        let footerRows = workSheet.rowCount + 2;

        workSheet.mergeCells(`B${footerRows}:M${footerRows}`);
        workSheet.mergeCells(`B${footerRows + 1}:M${footerRows + 1}`);
        workSheet.mergeCells(`B${footerRows + 2}:M${footerRows + 2}`);
        workSheet.mergeCells(`B${footerRows + 3}:M${footerRows + 3}`);
        workSheet.mergeCells(`B${footerRows + 4}:M${footerRows + 4}`);
        workSheet.mergeCells(`B${footerRows + 5}:M${footerRows + 5}`);
        workSheet.mergeCells(`B${footerRows + 6}:M${footerRows + 6}`);
        // Quantity
        workSheet.getRow(footerRows).getCell(1).value = "Total Quantity";
        workSheet
          .getRow(footerRows)
          .getCell(2).value = `${orderDetailData?.total_qty}`;

        // Price
        workSheet.getRow(footerRows + 1).getCell(1).value = "Total Price";
        workSheet
          .getRow(footerRows + 1)
          .getCell(2).value = `${orderDetailData?.total}`;

        // Coupon code
        workSheet.getRow(footerRows + 2).getCell(1).value = "Coupon Code";
        workSheet
          .getRow(footerRows + 2)
          .getCell(2).value = `${orderDetailData?.custom_code_discount_value}`;

        // Delivery Charges
        workSheet.getRow(footerRows + 3).getCell(1).value = "Delivery Charges";
        workSheet
          .getRow(footerRows + 3)
          .getCell(2).value = `${orderDetailData?.delivery_charges}`;

        // Discount
        workSheet.getRow(footerRows + 4).getCell(1).value = "Discount";
        workSheet
          .getRow(footerRows + 4)
          .getCell(2).value = `${orderDetailData?.discount_value}`;

        // Tax
        workSheet.getRow(footerRows + 5).getCell(1).value = "Tax";
        workSheet
          .getRow(footerRows + 5)
          .getCell(2).value = `${orderDetailData?.tax_value}`;

        // Grand Total
        workSheet.getRow(footerRows + 6).getCell(1).value = "Grand Total";
        workSheet
          .getRow(footerRows + 6)
          .getCell(2).value = `${orderDetailData?.grand_total}`;

        workSheet.getRow(footerRows + 6).getCell(2).border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };

        let totalFooterRows = footerRows + 7;
        while (footerRows < totalFooterRows) {
          workSheet.getRow(footerRows).getCell(1).font = {
            bold: true,
            size: 15,
          };

          workSheet.getRow(footerRows).getCell(2).font = {
            size: 14,
          };

          workSheet.getRow(footerRows).getCell(2).alignment = {
            vertical: "middle",
            horizontal: "right",
          };

          footerRows++;
        }

        // adding company name at the end of sheet
        let copyRightCell = workSheet.rowCount + 3;

        workSheet.mergeCells(`A${copyRightCell}:M${copyRightCell}`);

        workSheet.getRow(copyRightCell).getCell(1).value = getPoweredByText();

        workSheet.getRow(copyRightCell).getCell(1).font = {
          bold: true,
          size: 13,
        };

        workSheet.getRow(copyRightCell).getCell(1).alignment = {
          vertical: "middle",
          horizontal: "center",
        };

        workSheet.getRow(copyRightCell).getCell(1).border = {
          top: { style: "thick" },
          left: { style: "thick" },
          bottom: { style: "thick" },
          right: { style: "thick" },
        };

        workSheet.getColumn(1).width = 20;

        break;
      }
      case "StaffAttendanceList": {
        workSheet.mergeCells("A1:M1");
        workSheet.mergeCells("A2:M2");

        workSheet
          .getRow(1)
          .getCell(1).value = `${eatout_name} Staff Attendance`;
        workSheet.getRow(1).getCell(1).font = {
          size: 15,
        };

        workSheet.getRow(1).getCell(1).alignment = {
          vertical: "middle",
          horizontal: "center",
        };

        // adding column keys to add data
        workSheet.columns = tableData.map((obj) => ({
          key: obj.key,
        }));

        // adding table column heads
        workSheet.getRow(3).values = tableData.map((obj) => obj.header);

        // bold & width table header columns
        tableData.forEach((data, index) => {
          workSheet.getRow(3).getCell(index + 1).font = {
            bold: true,
          };

          workSheet.getColumn(index + 1).width = data?.header?.length + 12;
        });

        // loop through data and add each one to worksheet
        (listingData as StaffAttendanceResult[])?.forEach((item: any) => {
          let row: { [x: string]: string } = {};

          row["user_name"] = item?.user_name;

          if (item?.attendance?.length) {
            item?.attendance?.forEach((obj: any, index: number) => {
              row[tableData[index + 1].key] = obj?.action[0]?.clock_in
                ? formatTime(obj?.total_working_hours)
                : "- -";
            });
          }

          row["total_hours"] = formatTime(item?.total_hours);

          workSheet.addRow(row);
        });

        break;
      }
      case "StaffAttendanceDetails": {
        const staffDetails = listingData as SelectedStaffDetails;

        workSheet.mergeCells("A1:M1");
        workSheet.mergeCells("A2:M2");

        workSheet.mergeCells("B3:M3");
        workSheet.mergeCells("B4:M4");
        workSheet.mergeCells("B5:M5");
        workSheet.mergeCells("B6:M6");

        workSheet
          .getRow(1)
          .getCell(1).value = `${eatout_name} Staff Attendance Details`;
        workSheet.getRow(1).getCell(1).font = {
          size: 15,
        };

        workSheet.getRow(1).getCell(1).alignment = {
          vertical: "middle",
          horizontal: "center",
        };

        // starting order details information rows
        workSheet.getRow(3).getCell(1).value = "Name";
        workSheet.getRow(3).getCell(2).value = `${staffDetails?.userName}`;

        workSheet.getRow(4).getCell(1).value = "Date";
        workSheet.getRow(4).getCell(2).value = `${
          staffDetails?.attendanceRecord?.action_date &&
          moment(staffDetails?.attendanceRecord?.action_date).format(
            "Do MMMM YYYY"
          )
        }`;

        workSheet.getRow(5).getCell(1).value = "Hours Worked";
        workSheet.getRow(5).getCell(2).value = formatTime(
          staffDetails?.attendanceRecord?.total_working_hours
        );

        workSheet.getRow(6).getCell(1).value = "Total Break Time";
        workSheet.getRow(6).getCell(2).value = formatTime(
          staffDetails?.attendanceRecord?.total_break_time
        );

        // adding styles to details information rows
        let headerRows = 3;
        while (headerRows < 7) {
          workSheet.getRow(headerRows).getCell(1).font = {
            bold: true,
            size: 12,
          };

          workSheet.getRow(headerRows).getCell(2).font = {
            size: 12,
          };

          headerRows++;
        }

        // adding column keys to add data
        workSheet.columns = tableData.map((obj) => ({
          key: obj.key,
        }));

        // adding table column heads
        workSheet.getRow(workSheet.rowCount + 2).values = tableData.map(
          (obj) => obj.header
        );

        // loop through data and add each one to worksheet
        staffDetails?.attendanceRecord?.action?.forEach((item: any) => {
          let row: { [x: string]: string } = {};

          row["clock_in"] = item?.clock_in
            ? `${meridianDateFormat(item?.clock_in).formattedTime} \n ${
                meridianDateFormat(item?.clock_in).formattedDate
              }`
            : "- -";

          row["clock_out"] = item?.clock_out
            ? `${meridianDateFormat(item?.clock_out).formattedTime} \n ${
                meridianDateFormat(item?.clock_out).formattedDate
              }`
            : "- -";

          workSheet.addRow(row);

          // adding values for break timings
          item?.breaks?.forEach((breakTime: any, index: number) => {
            workSheet.getRow(workSheet.rowCount).getCell(index + 3).value = `${
              meridianDateFormat(breakTime.break_start).formattedTime
            }     -      ${
              meridianDateFormat(breakTime.break_end).formattedTime
            } \n ${meridianDateFormat(breakTime.break_start).formattedDate} - ${
              meridianDateFormat(breakTime.break_end).formattedDate
            }`;

            // aligning table cells right & time, date vertically
            workSheet.getRow(workSheet.rowCount).getCell(index + 3).alignment =
              {
                horizontal: "right",
                wrapText: true,
              };

            // setting width of break time columns
            workSheet.getColumn(index + 3).width = 25;
          });

          // right align clock_in/out time
          workSheet.getRow(workSheet.rowCount).getCell(1).alignment = {
            horizontal: "right",
            wrapText: true,
          };
          workSheet.getRow(workSheet.rowCount).getCell(2).alignment = {
            horizontal: "right",
            wrapText: true,
          };

          // table header bold
          workSheet.getRow(8).font = { bold: true };
        });

        // merging columns from C-M for break timings
        workSheet.mergeCells("B7:M7");
        workSheet.mergeCells("C8:M8");

        // specified the width of first column for whole sheet
        workSheet.getColumn(1).width = 30;
        workSheet.getColumn(2).width = 15;

        break;
      }

      default: {
        alert("No data to export!");
        break;
      }
    } //  end of switch statement

    // download the processed file
    const buffer = await workBook.xlsx.writeBuffer();

    saveAs(new Blob([buffer]), `${workBookName}.xlsx`);
  };

  if (
    exportType === "OrdersList" ||
    exportType === "ProductListing" ||
    exportType === "SEOListing" ||
    exportType === "Custom Report"
  ) {
    fileExport();

    // resetting value
    if (setExportType) {
      setExportType("import_export");
    }
  }

  return (
    <>
      {/* Return orders Listing export Button  UI */}
      {(exportType == "StaffAttendanceList" ||
        exportType == "StaffAttendanceDetails" ||
        exportType == "ReviewList" ||
        exportType == "Daily Sales" ||
        exportType == "Products" ||
        exportType === "ListingExport" ||
        exportType === "stockListing" ||
        exportType === "BusinessReview") && (
        <Button
          variant={"contained"}
          color={"secondary"}
          sx={headerButtonStyles}
          onClick={fileExport}
          disabled={disabled}
        >
          {buttonText}
        </Button>
      )}

      {/* Return order details icon button UI */}
      {exportType === "OrderDetail" && (
        <Stack
          direction="row"
          spacing={1}
          sx={{
            cursor: "pointer",
            alignItems: "center",
          }}
          onClick={fileExport}
        >
          <LibraryBooksTwoToneIcon className={classes.libraryBookIcon} />
          <Typography variant="h5" className={classes.excelExportFile}>
            Export Excel File
          </Typography>
        </Stack>
      )}
    </>
  );
};

export default ExcelExport;
